import { useQuery } from '@tanstack/react-query';
import { User } from 'lucide-react';
import { CircleFlag } from 'react-circle-flags';
import { useNavigate, useParams } from 'react-router';

import { ContentLayout } from '@/components/layouts';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ListGroup, ListGroupItem } from '@/components/ui/list-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { MEDIA_URL } from '@/config';
import { fetchTeamDetails } from '@/features/teams/api/getTeamDetails';
import { MatchHistory } from '@/features/teams/components/MatchHistory';
import { TeamBadges } from '@/features/teams/components/TeamBadges';
import { TeamHistory } from '@/features/teams/components/TeamHistory';
import TeamInfoList from '@/features/teams/components/TeamInfoList';
import { TeamMembers } from '@/features/teams/components/TeamMembers';
import TeamSchedule from '@/features/teams/components/TeamSchedule';
import { TeamSocials } from '@/features/teams/components/TeamSocials';
import { TeamStats } from '@/features/teams/components/TeamStats';
import TeamStatusBox from '@/features/teams/components/TeamStatusBox';

const TeamRoute = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: team, isLoading: isTeamLoading } = useQuery({
    queryKey: ['team', id],
    queryFn: () => fetchTeamDetails(id),
    enabled: !!id,
  });
  if (!id || !/^\d+$/.test(id)) {
    toast({
      description: 'Invalid team ID',
      variant: 'destructive',
    });
    navigate('/');
    return <Spinner size="lg" className="mx-auto" />;
  }
  if (!team && !isTeamLoading) {
    toast({
      description: 'Team does not exists',
      variant: 'destructive',
    });
    navigate('/');
    return <Spinner size="lg" className="mx-auto" />;
  }

  return (
    <ContentLayout
      title={`${team?.team_name}`}
      description="Team Profile for SCL."
    >
      <div className="shadow">
        <div className="relative h-[349px] w-full overflow-hidden rounded-t-xl border-x border-t border-border">
          {isTeamLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <img
              className="grid h-full w-full place-items-center object-cover"
              src={`${MEDIA_URL}/images/uploads/${team?.cover_photo}`}
              alt="Banner"
            />
          )}
        </div>
        <div className="w-full border-x border-b border-border bg-card p-4 !pb-6 !pt-0 sm:p-8">
          <div className="relative grid sm:flex sm:items-center sm:gap-x-6">
            {isTeamLoading ? (
              <Skeleton className="ring-block z-10 mx-auto -mt-16 h-32 w-32 !rounded-full !bg-muted text-xl ring-4 sm:mx-0" />
            ) : (
              <Avatar className="ring-block z-10 mx-auto -mt-16 h-32 w-32 bg-muted text-xl ring-4 sm:mx-0">
                <AvatarImage
                  src={`${MEDIA_URL}/images/uploads/${team?.team_photo}`}
                  alt={team?.team_name || ''}
                />
                <AvatarFallback className="bg-secondary">
                  <User />
                </AvatarFallback>
              </Avatar>
            )}
            <div className="mt-4 flex items-center justify-center gap-2 sm:mb-1.5 sm:mt-auto sm:justify-start">
              {isTeamLoading ? (
                <Skeleton className="h-6 w-96" />
              ) : (
                <>
                  <CircleFlag
                    countryCode={team?.country?.toLocaleLowerCase() || ''}
                    className="size-6"
                  />
                  <h2 className="whitespace-nowrap text-3xl font-semibold ">
                    {team?.team_name}
                  </h2>
                </>
              )}
            </div>
            {team && (
              <div className="ml-[1%] mt-8 flex w-full justify-between gap-4 sm:mt-4 sm:justify-end">
                <TeamBadges teamData={team} />
                <TeamSocials teamData={team} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-8 grid gap-y-8 lg:grid-cols-10 lg:gap-x-12 lg:gap-y-0 2xl:gap-x-24">
        <aside className="lg:col-span-3 lg:h-full lg:w-full">
          <div className="flex flex-col gap-y-4">
            <TeamStatusBox teamId={id} />
            {isTeamLoading ? (
              <ListGroup>
                <ListGroupItem>
                  <Skeleton className="h-2.5 w-full"></Skeleton>
                </ListGroupItem>
                <ListGroupItem>
                  <Skeleton className="h-2.5 w-full"></Skeleton>
                </ListGroupItem>
                <ListGroupItem>
                  <Skeleton className="h-2.5 w-full"></Skeleton>
                </ListGroupItem>
              </ListGroup>
            ) : (
              <>{team && <TeamInfoList teamData={team} />}</>
            )}
            {team?.description && (
              <div>
                <h3 className="mb-2 text-xs font-medium ">Description</h3>
                <div className="w-full overflow-x-auto whitespace-pre-wrap rounded-md border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
                  {team.description}
                </div>
              </div>
            )}
          </div>
        </aside>
        <div className="flex flex-col space-y-8 lg:col-span-7">
          <TeamSchedule teamId={id} />
          <TeamMembers teamId={id} />
          <TeamStats teamId={id} />
          <TeamHistory teamId={id} />
          <MatchHistory teamId={id} />
        </div>
      </div>
    </ContentLayout>
  );
};

export default TeamRoute;
